import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { eventsApi, type CreateEventBody, type UpdateEventBody } from '@/api/events.api';

export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  detail: (id: string) => [...eventKeys.all, 'detail', id] as const,
};

export function useEvents() {
  return useQuery({
    queryKey: eventKeys.lists(),
    queryFn: () => eventsApi.list(),
  });
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: eventKeys.detail(id || ''),
    queryFn: () => eventsApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEventBody) => eventsApi.create(body),
    onSuccess: () => {
      toast.success('Evento creado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al crear'),
  });
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateEventBody) => eventsApi.update(id, body),
    onSuccess: () => {
      toast.success('Evento actualizado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al actualizar'),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventsApi.remove(id),
    onSuccess: () => {
      toast.success('Evento eliminado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al eliminar'),
  });
}

export function useStartEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventsApi.start(id),
    onSuccess: () => {
      toast.success('Evento iniciado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al iniciar'),
  });
}

export function useEndEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventsApi.end(id),
    onSuccess: () => {
      toast.success('Evento finalizado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al finalizar'),
  });
}

export function useDrawEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventsApi.draw(id),
    onSuccess: () => {
      toast.success('Sorteo realizado');
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al sortear'),
  });
}
